import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { getListingsById } from '../../../../ducks/marketplaceData.duck';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { landingSectionSurfaceClassName } from '../../../LandingPage/landingSectionSurface';
import { ListingCard, NamedLink } from '../../../../components';

import css from './SectionAirportLounges.module.css';

const CARD_RENDER_SIZES = [
  '(max-width: 767px) 80vw',
  '(max-width: 1023px) 42vw',
  '(max-width: 1439px) 30vw',
  '300px',
].join(', ');

const SCROLL_EDGE_TOLERANCE = 4;

/**
 * Build the filter-pill label for a listing: "Lublin (LUZ)".
 *
 * A bare IATA code is unreadable to anyone who is not a frequent flyer — the row used to read
 * "ZAG · ZAD · EVN · WRO · WAW". Imported lounge addresses follow a stable shape,
 * "<Airport name> (<CODE>), <City>, <Country>", so the city is the second segment. The old
 * fallback took segment [0], which is the airport name, not the city.
 *
 * Falls back to whichever half is available, so a listing with an unusual address still gets a pill.
 */
const getPillLabel = listing => {
  const title = listing.attributes?.title || '';
  const address = listing.attributes?.publicData?.location?.address || '';

  const code = title.match(/\(([A-Z]{2,4})\)/)?.[1] || null;
  const segments = address.split(',').map(s => s.trim());
  const city = segments.length > 1 ? segments[1] : segments[0] || null;

  if (city && code) return `${city} (${code})`;
  return city || code;
};

const SectionAirportLounges = ({ sectionId, className, rootClassName, landingSurface }) => {
  const intl = useIntl();
  const scrollerRef = useRef(null);
  const pillsRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  const rowState = useSelector(state => state.LandingPage?.listingRows?.['lounge'] || {});
  const { ids = [] } = rowState;
  const listings = useSelector(state => getListingsById(state, ids));

  // Derive unique location pills from the actual listings
  const locationPills = useMemo(() => {
    const labels = listings.map(getPillLabel).filter(Boolean);
    return [...new Set(labels)];
  }, [listings]);

  const filteredListings = useMemo(() => {
    if (activeFilter === 'all') return listings;
    return listings.filter(l => getPillLabel(l) === activeFilter);
  }, [listings, activeFilter]);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > SCROLL_EDGE_TOLERANCE);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - SCROLL_EDGE_TOLERANCE);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, filteredListings.length]);

  // Reset scroll position when filter changes
  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollLeft = 0;
  }, [activeFilter]);

  // The section now stays mounted across refetches (see the gate below), so a refetch that drops
  // the city behind the active pill would otherwise leave an empty rail. Fall back to "all".
  useEffect(() => {
    if (activeFilter !== 'all' && !locationPills.includes(activeFilter)) {
      setActiveFilter('all');
    }
  }, [locationPills, activeFilter]);

  const scrollByPage = dir => () => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: Math.round(el.clientWidth * 0.9) * dir, behavior: 'smooth' });
  };

  // Same predicate as SectionLandingListingRows: keep already-loaded lounges mounted while a
  // refetch runs, so hydration does not tear down server-rendered DOM.
  if (listings.length === 0) return null;

  const showPills = locationPills.length >= 1;

  return (
    <section
      id={sectionId}
      className={classNames(
        css.root,
        landingSectionSurfaceClassName(landingSurface),
        rootClassName,
        className
      )}
    >
      <div className={css.wrapper}>
        {/* Header */}
        <div className={css.header}>
          <div className={css.eyebrow}>
            <span className={css.eyebrowIcon} aria-hidden="true">
              ✈
            </span>
            {/* Deliberately not "Neu auf cowork24" — the lounge banner directly below this
                section already carries that eyebrow, so the visitor read it twice in one scroll. */}
            <span>
              <FormattedMessage id="SectionAirportLounges.eyebrow" />
            </span>
          </div>
          <div className={css.titleRow}>
            <h2 className={css.title}>
              <FormattedMessage id="SectionAirportLounges.title" />
            </h2>
            <NamedLink name="AirportLoungeLandingPage" className={css.viewAll}>
              <span className={css.viewAllLabel}>
                <FormattedMessage id="SectionLandingListingRows.viewAll" />
              </span>
              <ChevronRight className={css.viewAllIcon} aria-hidden="true" />
            </NamedLink>
          </div>
          <p className={css.subtitle}>
            <FormattedMessage id="SectionAirportLounges.subtitle" />
          </p>
        </div>

        {/* Location filter pills */}
        {showPills && (
          <div
            className={css.pillsRow}
            ref={pillsRef}
            aria-label={intl.formatMessage({ id: 'SectionAirportLounges.filterLabel' })}
          >
            <button
              type="button"
              className={classNames(css.pill, { [css.pillActive]: activeFilter === 'all' })}
              onClick={() => setActiveFilter('all')}
              aria-pressed={activeFilter === 'all'}
            >
              <FormattedMessage id="SectionAirportLounges.filterAll" />
            </button>
            {locationPills.map(label => (
              <button
                key={label}
                type="button"
                className={classNames(css.pill, { [css.pillActive]: activeFilter === label })}
                onClick={() => setActiveFilter(label)}
                aria-pressed={activeFilter === label}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Scrollable listing cards */}
        <div className={css.scrollerWrap}>
          <button
            type="button"
            className={classNames(css.navButton, css.navButtonLeft, {
              [css.navButtonHidden]: !canScrollLeft,
            })}
            aria-label={intl.formatMessage({ id: 'SectionLandingListingRows.scrollPrevLabel' })}
            onClick={scrollByPage(-1)}
            tabIndex={canScrollLeft ? 0 : -1}
          >
            <ChevronLeft aria-hidden="true" />
          </button>
          <button
            type="button"
            className={classNames(css.navButton, css.navButtonRight, {
              [css.navButtonHidden]: !canScrollRight,
            })}
            aria-label={intl.formatMessage({ id: 'SectionLandingListingRows.scrollNextLabel' })}
            onClick={scrollByPage(1)}
            tabIndex={canScrollRight ? 0 : -1}
          >
            <ChevronRight aria-hidden="true" />
          </button>

          <div className={css.scroller} ref={scrollerRef}>
            <ul
              className={css.list}
              aria-label={intl.formatMessage({ id: 'SectionAirportLounges.title' })}
            >
              {filteredListings.map((listing, index) => (
                <li key={listing.id.uuid} className={css.cardWrap}>
                  <div className={css.cardShell}>
                    <ListingCard
                      className={css.listingCard}
                      listing={listing}
                      renderSizes={CARD_RENDER_SIZES}
                      intl={intl}
                      highImagePriority={index === 0}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SectionAirportLounges;
